/**
 * Route Royal Hero Component
 * 
 * Marketing hero section for Route Royal main website
 */

'use client';

import Image from 'next/image';
import type { HeroProps } from '@routeroyal/theme-core';

function isSafeHref(href: string): boolean {
  const lower = href.toLowerCase().trim();
  if (lower.startsWith('/')) return true;
  if (lower.startsWith('https://')) return true;
  if (lower.startsWith('http://')) return true;
  return false;
}

export function RouteRoyalHero({
  title,
  subtitle,
  backgroundImage,
  ctaText,
  ctaLink,
}: HeroProps) {
  // Default banner image for Route Royal marketing
  const bannerImage = backgroundImage || 'https://res.cloudinary.com/elementary-media/image/upload/v1763569212/damodash_a_modern_interpretation_of_a_vintage_risograph_print_4f4a9445-a6dd-4458-b6ea-28478a895ef6_2_hp4aas.gif';
  
  return (
    <div className="relative min-h-screen flex items-center justify-center bg-white overflow-hidden">
      {/* Background Banner Image */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="relative inline-block">
          <Image
            src={bannerImage}
            alt={title || 'Route Royal Banner'}
            width={600}
            height={338}
            className="w-[600px] h-auto"
            priority
            unoptimized
          />
        </div>
      </div>
      
      {/* Content Overlay (if title/subtitle provided) */}
      {(title || subtitle) && (
        <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8">
          {title && (
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-4">
              {title}
            </h1>
          )}
          {subtitle && (
            <p className="hero-description text-xl sm:text-2xl text-gray-600 mb-8" data-speakable>
              {subtitle}
            </p>
          )}
          {ctaText && ctaLink && isSafeHref(ctaLink) && (
            <a
              href={ctaLink}
              className="inline-block px-8 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              {ctaText}
            </a>
          )}
        </div>
      )}
    </div>
  );
}

